import React, { useState, useEffect, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Password } from "primereact/password";
import { toast } from "react-toastify";

import { User } from "../../../types/user";
import { Firm } from "../../../types/firm";
import { Customer } from "../../../types/customer";
import { getTurkishRoleName, RoleType } from "../../../types/role";
import { Search, Save, X, Building2, User as UserIcon, Plus } from "lucide-react";
import { IconButton, IconTextButton } from "../../../components/ButtonComponents";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { CustomSelectInput } from "../../../components/CustomSelectInput";
import { Filter } from "../../../types";

import { useFirmStore } from "../../../store/firmStore";
import { useRoleStore } from "../../../store/roleStore";
import { useCustomerStore } from "../../../store/customerStore";
import { AccountPrintSettings } from "../../AccountPrintSettings";


// --------------------------------------

interface UserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onSave: (userData: Omit<User, "id">) => void;
}

/**
 * TypeScript Hatalarını Çözmek İçin:
 * User tipinden gelen tüm objeleri (firm, account, role) Omit ile atıyoruz.
 * Sadece formda bize lazım olan düz alanları ekliyoruz.
 */
export type UserFormData = Omit<User, "id" | "firm" | "account" | "role"> & {
  roleName: string;
  firmName: string;
  accountName: string | null;
};

const initialFormData: UserFormData = {
  firmId: 0,
  firmName: "",
  accountId: undefined,
  accountName: null,
  roleId: 0,
  roleName: "",
  name: "",
  email: "",
  password: "",
};

export const UserDialog: React.FC<UserDialogProps> = ({ isOpen, onClose, user, onSave }) => {
  const { data: firmData, fetchDataDynamic: fetchFirms, loading: firmLoading } = useFirmStore();
  const { fetchDataDynamic: fetchAccounts, loading: accountLoading } = useCustomerStore();
  const { fetchDataDynamic: fetchRoles } = useRoleStore();

  const [roleOptions, setRoleOptions] = useState<{ id: number; name: string; originalName: string }[]>([]);
  const [formData, setFormData] = useState<UserFormData>(initialFormData);

  const firmSelectDialog = useSelectDialog<Firm>();
  const accountSelectDialog = useSelectDialog<Customer>();

  useEffect(() => {
    if (!isOpen) return;
    fetchRoles().then((res: any) => {
      const items = res?.items || [];
      const formatted = items.map((r: any) => ({
        id: r.id,
        name: getTurkishRoleName(r.name),
        originalName: r.name
      }));
      setRoleOptions(formatted);
    });
  }, [isOpen, fetchRoles]);

  useEffect(() => {
    if (!isOpen) return;
    if (user) {
      setFormData({
        firmId: user.firmId || 0,
        firmName: (user.firm as Firm)?.name || "",
        accountId: user.accountId,
        accountName: (user.account as Customer)?.name || null,
        roleId: user.roleId,
        roleName: (user.role as RoleType)?.name || "",
        name: user.name,
        email: user.email,
        password: "",
      });
    } else {
      setFormData(initialFormData);
    }
  }, [user, isOpen]);

  // Yardımcı input değişim fonksiyonu
  const handleChange = (field: keyof UserFormData, value: any) => {
    setFormData(p => ({ ...p, [field]: value }));
  };

  async function openFirmSelection() {
    await fetchFirms({ pageIndex: 1, pageSize: 10 });
    const selected = await firmSelectDialog.open({
      title: "Firma Seç",
      data: firmData,
      columns: [{ field: "id", header: "ID" }, { field: "name", header: "Firma Adı" }],
      loading: firmLoading,
      onFilter: async (params) => {
        const res = await fetchFirms(params);
        return { data: res.items ?? [], total: res.total ?? 0 };
      },
    });

    if (selected) {
      setFormData(prev => ({
        ...prev,
        firmId: selected.id,
        firmName: selected.name,
        accountId: undefined,
        accountName: null,
      }));
    }
  }

  async function openAccountSelection() {
    if (!formData.firmId) {
      toast.warn("Lütfen önce bir Firma seçin.");
      return;
    }

    const firmFilter: Filter = {
      logic: "and",
      filters: [{ field: "firmId", operator: "eq", value: formData.firmId }],
    };

    const resInitial = await fetchAccounts({ pageIndex: 1, pageSize: 10, filter: firmFilter });

    const selected = await accountSelectDialog.open({
      title: "Müşteri Seç",
      data: resInitial.items || [],
      columns: [{ field: "id", header: "ID" }, { field: "name", header: "Müşteri Adı" }],
      loading: accountLoading,
      onFilter: async (params) => {
        const combinedFilter: Filter = {
          logic: "and",
          filters: [...firmFilter.filters, ...(params.filter?.filters || [])],
        };
        const res = await fetchAccounts({ ...params, filter: combinedFilter });
        return { data: res.items ?? [], total: res.total ?? 0 };
      },
    });

    if (selected) {
      setFormData(prev => ({ ...prev, accountId: selected.id, accountName: selected.name }));
    }
  }

  const isCustomerRole = formData.roleName === "customer" || formData.roleName === "Müşteri";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || (!user && !formData.password)) {
      toast.error("Zorunlu alanları doldurun.");
      return;
    }

    if (isCustomerRole && !formData.accountId) {
      toast.error("Müşteri rolü için hesap seçimi zorunludur.");
      return;
    }

    // Backend'e giderken veriyi tekrar User tipine (objeli hali) mapliyoruz
    const payload: Omit<User, "id"> = {
      ...formData,
      firm: { id: formData.firmId, name: formData.firmName } as Firm,
      account: formData.accountId ? { id: formData.accountId, name: formData.accountName } as Customer : null,
      role: { id: formData.roleId, name: formData.roleName } as RoleType,
    };

    onSave(payload);
  };

  return (
    <>
      <Dialog
        header={user ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}
        visible={isOpen}
        onHide={onClose}
        modal
        className="w-full md:w-6/12"
        footer={
          <div className="flex justify-end gap-3 pt-4 border-t">
            <IconTextButton onClick={onClose} icon={X} className="btn-cancel">İptal</IconTextButton>
            <IconTextButton onClick={handleSubmit} icon={user ? Save : Plus} className="btn-new">
              {user ? "Güncelle" : "Ekle"}
            </IconTextButton>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Ad Soyad *</label>
            <InputText value={formData.name} onChange={(e) => handleChange("name", e.target.value)} />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">E-posta *</label>
            <InputText value={formData.email} onChange={(e) => handleChange("email", e.target.value)} />
          </div>

          <div className="flex flex-col gap-1">
            <CustomSelectInput
              label="Firma *"
              value={formData.firmName || ""}
              placeholder="Firma Seç..."
              icon={Building2}
              onClick={openFirmSelection}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Rol *</label>
            <Dropdown
              value={formData.roleId}
              options={roleOptions}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => {
                const r = roleOptions.find(x => x.id === e.value);
                setFormData(p => ({
                  ...p,
                  roleId: e.value,
                  roleName: r?.originalName || "",
                  ...(r?.originalName !== "customer" && { accountId: undefined, accountName: null })
                }));
              }}
              placeholder="Seçiniz"
            />
          </div>

          <div className={`flex flex-col gap-1 ${!isCustomerRole ? 'opacity-50' : ''}`}>
            <CustomSelectInput
              label={`Bağlı Müşteri (Hesap) ${isCustomerRole ? "*" : ""}`}
              value={formData.accountName || ""}
              placeholder="Hesap Seç..."
              icon={UserIcon}
              onClick={isCustomerRole ? openAccountSelection : () => { }}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Şifre {!user && "*"}</label>
            <Password value={formData.password} onChange={(e) => handleChange("password", e.target.value)} toggleMask feedback={false} className="w-full" />
          </div>
        </div>
        
        {user?.id && (
          <div className="mt-8 border-t border-borderColor pt-4">
            <AccountPrintSettings userId={user.id} />
          </div>
        )}
      </Dialog>
      {firmSelectDialog.modal}
      {accountSelectDialog.modal}
    </>
  );
};